import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const SectorsList = () => {
    useTrackScreenVisit('system sectors');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<Sector>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: sector => sector.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 320,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'type',
            header: t('Type'),
            accessorFn: sector => sector.sectorType,
            cell: ({row}) => (
                <Box>{row.original.sectorType ? t(`Sector Type ${row.original.sectorType}`) : t('(Not set)')}</Box>
            ),
            size: 260,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'sectorType'}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: sector => sector.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: sector => sector.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-sectors',
        columns,
        getRowId: sector => sector.id!,
        onRowClick: sector => navigate(`/system/sectors/${sector.id}`),
        initialSorting: [{id: 'name', desc: false}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await SectorService.getAllSectors({
                name: searchQuery ? [searchQuery] : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    return (
        <SettingsListPage title={t('Sectors')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create sector')}
                            onClick={() => navigate('/system/sectors/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default SectorsList;
